import { lazy, Suspense, useState } from 'react'
import { Routes, Route, useNavigate } from 'react-router-dom'
import { AuthProvider } from './contexts/AuthContext'
import { LocaleContentProvider } from './contexts/LocaleContentContext'
import { ReferralCodeProvider } from './contexts/ReferralCodeContext'
import I18nProvider from './components/I18nProvider'
import { useAuthStore } from './stores/authStore'
import Layout from './components/Layout'
import ProtectedRoute from './components/ProtectedRoute'
import RootRoute from './components/RootRoute'
import RouteTracker from './components/RouteTracker'
import CookieConsent from './components/CookieConsent'
import PWAInstallPrompt from './components/PWAInstallPrompt'
import OfflineIndicator from './components/OfflineIndicator'
import ApiAuthInitializer from './components/ApiAuthInitializer'
import SessionMonitor from './components/SessionMonitor'
import { SkipLink } from './components/A11y'
import { useGlobalShortcuts, useKeyboardShortcuts } from './hooks/useKeyboardShortcuts'

// Route-level code splitting (change 048, CWV): every page except the
// landing path (RootRoute -> HomePage) loads on demand. This keeps the
// initial bundle small so marketing pages hit LCP/TBT targets on mobile.
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const Chat = lazy(() => import('./pages/Chat'))
const Pricing = lazy(() => import('./pages/Pricing'))
const Subscription = lazy(() => import('./pages/Subscription'))
const Profile = lazy(() => import('./pages/Profile'))
const Settings = lazy(() => import('./pages/Settings'))
const ApiKeysPage = lazy(() => import('./pages/ApiKeysPage'))
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'))
const TermsOfService = lazy(() => import('./pages/TermsOfService'))
const OAuthCallback = lazy(() => import('./pages/OAuthCallback'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const SolutionsPage = lazy(() => import('./pages/SolutionsPage'))
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'))
const FAQPage = lazy(() => import('./pages/FAQPage'))
const ReferralDashboard = lazy(() => import('./pages/ReferralDashboard'))
const InvitePage = lazy(() => import('./pages/InvitePage'))
const MarketingInvitePage = lazy(() => import('./pages/MarketingInvitePage'))
const MassimeSearchPage = lazy(() => import('./pages/MassimeSearchPage'))
const MassimaDetailPage = lazy(() => import('./pages/MassimaDetailPage'))
const KeyboardShortcutsHelp = lazy(() => import('./components/KeyboardShortcutsHelp'))

// Articles & dev docs (change 049): 4 consumer articles + /guide landing + 5 dev pages
const GuidePage = lazy(() => import('./pages/GuidePage'))
const GuideLeggereSentenzaPage = lazy(() => import('./pages/GuideLeggereSentenzaPage'))
const GuideMassimaSentenzaPage = lazy(() => import('./pages/GuideMassimaSentenzaPage'))
const GuideQuandoServeAvvocatoPage = lazy(() => import('./pages/GuideQuandoServeAvvocatoPage'))
const GuideCassazioneComeFunzionaPage = lazy(() => import('./pages/GuideCassazioneComeFunzionaPage'))
const GuideUsareChatgptPage = lazy(() => import('./pages/GuideUsareChatgptPage'))
const GuideLeggeIAItalia2025Page = lazy(() => import('./pages/GuideLeggeIAItalia2025Page'))
const GuideArt2043Page = lazy(() => import('./pages/GuideArt2043Page'))
const GuideLicenziamentoPage = lazy(() => import('./pages/GuideLicenziamentoPage'))
const DevelopersPage = lazy(() => import('./pages/DevelopersPage'))
const DevelopersMcpPage = lazy(() => import('./pages/DevelopersMcpPage'))
const DevelopersApiPage = lazy(() => import('./pages/DevelopersApiPage'))
const DevelopersCliPage = lazy(() => import('./pages/DevelopersCliPage'))
const DevelopersFaqPage = lazy(() => import('./pages/DevelopersFaqPage'))

// Minimal route fallback — mirrors RootRoute's auth-loading spinner so
// lazy-route loads don't flash a blank page or shift layout
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  )
}

function AppContent() {
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [isShortcutsHelpOpen, setIsShortcutsHelpOpen] = useState(false)

  // Global keyboard shortcuts registry
  const shortcuts = useGlobalShortcuts(navigate, isAuthenticated, {
    onOpenShortcutsHelp: () => setIsShortcutsHelpOpen(true),
    onOpenSettings: () => navigate('/profile'),
    onCloseModal: () => {
      if (isShortcutsHelpOpen) setIsShortcutsHelpOpen(false)
    },
  })

  // Register global shortcuts
  useKeyboardShortcuts(shortcuts, { enabled: true, context: 'global' })

  return (
    <I18nProvider>
      <>
        {/* Initialize API authentication with Clerk tokens */}
        <ApiAuthInitializer />

        {/* Skip to main content link for keyboard users (WCAG 2.4.1) */}
        <SkipLink />

        {/* Track page views on route changes */}
        <RouteTracker />

        {/* Monitor Clerk session and show expiration warnings */}
        <SessionMonitor />

        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<Layout />}>
            <Route index element={<RootRoute />} />
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            {/* Desktop deep-link target (TASK-C1): /signup?ref=sovereign-ai&plan=desktop */}
            <Route path="signup" element={<Register />} />
            <Route path="invite" element={<InvitePage />} />
            <Route path="marketing/invite" element={<MarketingInvitePage />} />
            <Route path="about" element={<AboutPage />} />
            <Route path="solutions" element={<SolutionsPage />} />
            <Route path="how-it-works" element={<HowItWorksPage />} />
            <Route path="faq" element={<FAQPage />} />
            <Route path="pricing" element={<Pricing />} />
            <Route path="privacy" element={<PrivacyPolicy />} />
            <Route path="terms" element={<TermsOfService />} />

            {/* Public Cassazione massime search engine (change 033) */}
            <Route path="massime" element={<MassimeSearchPage />} />
            <Route path="massima/:rv" element={<MassimaDetailPage />} />

            {/* Consumer articles (change 049) */}
            <Route path="guide" element={<GuidePage />} />
            <Route path="guide/leggere-una-sentenza" element={<GuideLeggereSentenzaPage />} />
            <Route path="guide/massima-e-sentenza" element={<GuideMassimaSentenzaPage />} />
            <Route path="guide/quando-serve-avvocato" element={<GuideQuandoServeAvvocatoPage />} />
            <Route path="guide/cassazione-come-funziona" element={<GuideCassazioneComeFunzionaPage />} />
            <Route path="guide/usare-con-chatgpt" element={<GuideUsareChatgptPage />} />
            <Route path="guide/legge-ia-italia-2025" element={<GuideLeggeIAItalia2025Page />} />
            <Route path="guide/art-2043-responsabilita-aquiliana" element={<GuideArt2043Page />} />
            <Route path="guide/licenziamento-giusta-causa" element={<GuideLicenziamentoPage />} />

            {/* Developer docs (change 049) */}
            <Route path="developers" element={<DevelopersPage />} />
            <Route path="developers/mcp" element={<DevelopersMcpPage />} />
            <Route path="developers/api" element={<DevelopersApiPage />} />
            <Route path="developers/cli" element={<DevelopersCliPage />} />
            <Route path="developers/faq" element={<DevelopersFaqPage />} />

            {/* Protected Routes */}
            <Route
              path="dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="chat"
              element={
                <ProtectedRoute>
                  <Chat />
                </ProtectedRoute>
              }
            />
            <Route
              path="subscription"
              element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            <Route
              path="referrals"
              element={
                <ProtectedRoute>
                  <ReferralDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="api-keys"
              element={
                <ProtectedRoute>
                  <ApiKeysPage />
                </ProtectedRoute>
              }
            />

            {/* OAuth Callbacks */}
            <Route path="auth/callback" element={<OAuthCallback />} />
          </Route>
        </Routes>
        </Suspense>

        {/* Global Cookie Consent Banner */}
        <CookieConsent />

        {/* PWA Install Prompt */}
        <PWAInstallPrompt />

        {/* Offline Status Indicator */}
        <OfflineIndicator />

        {/* Global Keyboard Shortcuts Help (⌘/) */}
        <Suspense fallback={null}>
          <KeyboardShortcutsHelp
            isOpen={isShortcutsHelpOpen}
            onClose={() => setIsShortcutsHelpOpen(false)}
            shortcuts={shortcuts}
          />
        </Suspense>
      </>
    </I18nProvider>
  )
}

function App() {
  return (
    <AuthProvider>
      <LocaleContentProvider>
        <ReferralCodeProvider>
          <AppContent />
        </ReferralCodeProvider>
      </LocaleContentProvider>
    </AuthProvider>
  )
}

export default App
